/**
 * PDF Viewer component using react-pdf.
 * Displays all pages in a scrollable container.
 * Supports zoom, download, and print.
 * Responsive: defaults to fit-width for large PDFs.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Document, Page, pdfjs } from 'react-pdf';
import {
  ZoomIn,
  ZoomOut,
  Download,
  Printer,
  Loader2,
  AlertCircle,
  Maximize2,
} from 'lucide-react';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Set up PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface PDFViewerProps {
  url: string;
  fileName: string;
  className?: string;
}

const ZOOM_LEVELS = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 2];

export function PDFViewer({ url, fileName, className }: PDFViewerProps) {
  const { t } = useTranslation('ats');
  const [numPages, setNumPages] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isPrinting, setIsPrinting] = useState<boolean>(false);

  // Zoom state: null = fit-width (responsive), number = fixed zoom level index
  const [zoomMode, setZoomMode] = useState<number | null>(null);

  // Container and page dimensions for fit-width calculation
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const [pageWidth, setPageWidth] = useState<number>(0);

  const containerRef = useRef<HTMLDivElement>(null);

  // Calculate fit-to-width scale
  const fitWidthScale = containerWidth && pageWidth ? (containerWidth - 32) / pageWidth : 1;

  // Determine actual scale based on zoom mode
  const scale = zoomMode === null ? fitWidthScale : ZOOM_LEVELS[zoomMode];

  const onDocumentLoadSuccess = useCallback(
    async (pdf: {
      numPages: number;
      getPage: (pageNum: number) => Promise<{ getViewport: (params: { scale: number }) => { width: number } }>;
    }) => {
      setNumPages(pdf.numPages);
      setLoading(false);
      setError(null);

      // Get first page dimensions for fit-width calculation
      try {
        const page = await pdf.getPage(1);
        const viewport = page.getViewport({ scale: 1 });
        setPageWidth(viewport.width);
      } catch (err) {
        console.error('Failed to get page dimensions:', err);
      }
    },
    []
  );

  const onDocumentLoadError = useCallback((error: Error) => {
    setLoading(false);
    setError(error.message || t('pdfViewer.loadFailed'));
  }, [t]);

  // Measure container width on mount and resize
  useEffect(() => {
    const updateContainerWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };

    updateContainerWidth();

    const resizeObserver = new ResizeObserver(updateContainerWidth);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const zoomIn = useCallback(() => {
    if (zoomMode === null) {
      // Switch from fit-width to 100% fixed zoom
      setZoomMode(3);
    } else {
      // Increase fixed zoom level
      setZoomMode((prev) => Math.min((prev ?? 3) + 1, ZOOM_LEVELS.length - 1));
    }
  }, [zoomMode]);

  const zoomOut = useCallback(() => {
    if (zoomMode === null) {
      // Switch from fit-width to 75% fixed zoom
      setZoomMode(2);
    } else if (zoomMode === 0) {
      // At minimum, stay at 25%
      return;
    } else {
      // Decrease fixed zoom level
      setZoomMode((prev) => Math.max((prev ?? 3) - 1, 0));
    }
  }, [zoomMode]);

  const setFitWidth = useCallback(() => {
    setZoomMode(null);
  }, []);

  const handleDownload = useCallback(() => {
    // Open signed URL in new tab for download
    window.open(url, '_blank');
  }, [url]);

  const handlePrint = useCallback(() => {
    setIsPrinting(true);

    const style = document.createElement('style');
    style.innerHTML = `@media print { body > * { display: none !important; } #pdf-print-area { display: block !important; } }`;
    document.head.appendChild(style);

    const printArea = document.createElement('div');
    printArea.id = 'pdf-print-area';
    printArea.style.display = 'none';

    const canvases = containerRef.current?.querySelectorAll('canvas');
    if (!canvases || canvases.length === 0) {
      document.head.removeChild(style);
      setIsPrinting(false);
      return;
    }

    const loadPromises = Array.from(canvases).map((canvas) =>
      new Promise<void>((resolve) => {
        const img = document.createElement('img');
        img.style.cssText = 'width:100%;display:block;page-break-after:always;';
        img.onload = () => resolve();
        img.onerror = () => resolve();
        img.src = canvas.toDataURL('image/png');
        printArea.appendChild(img);
      })
    );

    document.body.appendChild(printArea);

    Promise.all(loadPromises).then(() => {
      window.print();
      document.body.removeChild(printArea);
      document.head.removeChild(style);
      setIsPrinting(false);
    });
  }, []);

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full min-h-[400px] bg-gray-50 rounded-lg border border-gray-200">
        <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
        <p className="text-gray-700 font-medium mb-2">{t('pdfViewer.loadFailed')}</p>
        <p className="text-gray-500 text-sm mb-4">{error}</p>
        <button
          onClick={() => {
            setError(null);
            setLoading(true);
          }}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
        >
          {t('pdfViewer.retry')}
        </button>
      </div>
    );
  }

  return (
    <div className={className || "flex flex-col h-full bg-gray-100 rounded-lg border border-gray-200 overflow-hidden"}>
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-2 bg-white border-b border-gray-200">
        {/* Page Count */}
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-700">
            {loading ? t('pdfViewer.loading') : t('pdfViewer.pages', { count: numPages })}
          </span>
        </div>

        {/* Zoom Controls */}
        <div className="flex items-center gap-2">
          <button
            onClick={zoomOut}
            disabled={zoomMode === 0}
            className="p-1.5 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            title={t('pdfViewer.zoomOut')}
          >
            <ZoomOut className="h-5 w-5" />
          </button>
          <span className="text-sm text-gray-700 min-w-[70px] text-center">
            {zoomMode === null ? t('pdfViewer.fitWidth') : `${Math.round(ZOOM_LEVELS[zoomMode] * 100)}%`}
          </span>
          <button
            onClick={zoomIn}
            disabled={zoomMode !== null && zoomMode >= ZOOM_LEVELS.length - 1}
            className="p-1.5 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            title={t('pdfViewer.zoomIn')}
          >
            <ZoomIn className="h-5 w-5" />
          </button>
          <button
            onClick={setFitWidth}
            disabled={zoomMode === null}
            className="p-1.5 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            title={t('pdfViewer.fitToWidth')}
          >
            <Maximize2 className="h-5 w-5" />
          </button>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1">
          <button
            onClick={handleDownload}
            className="p-1.5 rounded hover:bg-gray-100"
            title={`Download ${fileName}`}
          >
            <Download className="h-5 w-5" />
          </button>
          <button onClick={handlePrint} disabled={isPrinting} className="p-1.5 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed" title={t('pdfViewer.print')}>
            {isPrinting ? <Loader2 className="h-5 w-5 animate-spin" /> : <Printer className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* PDF Container */}
      <div ref={containerRef} className="flex-1 overflow-auto p-4 bg-gray-200">
        {loading && (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        )}
        <div className="flex flex-col items-center gap-8 min-h-full">
          <Document
            file={url}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={onDocumentLoadError}
            loading={null}
            externalLinkTarget="_blank"
          >
            {Array.from(new Array(numPages), (_, index) => (
              <div key={`page_${index + 1}`} className="shadow-lg mb-2">
                <Page
                  pageNumber={index + 1}
                  scale={scale}
                  renderTextLayer={true}
                  renderAnnotationLayer={true}
                  className="bg-white"
                />
              </div>
            ))}
          </Document>
        </div>
      </div>
    </div>
  );
}

export default PDFViewer;
